import { Timestamp, doc, writeBatch } from 'firebase/firestore';
import { InboxMessageSchema, type InboxMessage } from '@kilat/shared';

import { db } from '../firebase.js';
import { createRepo } from './_base.js';

/**
 * In-app inbox READER for the dashboard. Two sources, one list:
 *
 *   users/{uid}/inbox/{msgId}                   — this person's own messages:
 *     the operator digest, first-win, billing (trial ending, invoice, past
 *     due), new bookings, low reviews, milestones.
 *   platformBrands/{brandId}/adminInbox/{msgId} — the brand's SHARED
 *     super-admin queue (new agent/recruiter applications, payout batch due).
 *     Super-admins exist only as auth claims, so the server has no per-person
 *     address for them; `notifyBrandAdmins` writes here instead and every
 *     super-admin of the brand reads it.
 *
 * Reads are ALWAYS bounded (`orderBy(createdAt desc).limit(N)`) via
 * `listSnapshotBounded`. Marking-read is a field-narrowed `readAt` stamp — the
 * only client write the rules allow on either path.
 */
function inboxRepoAt(path: string) {
  return createRepo({ path, schema: InboxMessageSchema, name: path });
}

/** Hard cap on inbox messages held by a single listener. */
export const INBOX_PAGE_SIZE = 30;

/** Which inboxes this viewer reads. The admin queue only for a super-admin of the brand. */
export function inboxPathsFor(uid: string, superAdminBrandId: string | null): string[] {
  const paths = [`users/${uid}/inbox`];
  if (superAdminBrandId) paths.push(`platformBrands/${superAdminBrandId}/adminInbox`);
  return paths;
}

/** A message plus the collection it lives in, so mark-read writes the right doc. */
export interface InboxEntry {
  readonly message: InboxMessage;
  readonly path: string;
}

/**
 * Listen to every source and emit ONE merged list, newest first, capped at
 * `INBOX_PAGE_SIZE`. Emits only once every source has answered at least once,
 * so the list never flashes "empty" while the second listener is still
 * connecting. Returns a single unsubscribe for all of them.
 */
export function subscribeInboxes(
  paths: readonly string[],
  onEntries: (entries: InboxEntry[]) => void,
  onError: (err: Error) => void,
): () => void {
  const latest = new Map<string, InboxMessage[]>();
  const emit = () => {
    if (latest.size < paths.length) return;
    const merged: InboxEntry[] = [];
    for (const [path, messages] of latest) {
      for (const message of messages) merged.push({ message, path });
    }
    merged.sort((a, b) => tsMs(b.message.createdAt) - tsMs(a.message.createdAt));
    onEntries(merged.slice(0, INBOX_PAGE_SIZE));
  };
  const unsubs = paths.map((path) =>
    inboxRepoAt(path).listSnapshotBounded(
      { orderBy: [['createdAt', 'desc']], limitTo: INBOX_PAGE_SIZE },
      (messages) => {
        latest.set(path, messages);
        emit();
      },
      onError,
    ),
  );
  return () => {
    for (const u of unsubs) u();
  };
}

/** Stamp `readAt` on several messages, across sources, in one batched write. */
export async function markInboxRead(entries: readonly InboxEntry[]): Promise<void> {
  if (entries.length === 0) return;
  const batch = writeBatch(db);
  const now = Timestamp.now();
  for (const e of entries) batch.update(doc(db, e.path, e.message.id), { readAt: now });
  await batch.commit();
}

function tsMs(ts: { toMillis?: () => number; seconds: number } | undefined): number {
  if (!ts) return 0;
  return typeof ts.toMillis === 'function' ? ts.toMillis() : ts.seconds * 1000;
}
